<template>
  <section class="event-review-section">
    <div class="event-review-section__title">
      <span>Zones ({{ newEvent.zones.length }})</span>
    </div>
    <div v-if="newEvent.zones.length" class="event-review-section__content">
      <table class="zone-sensors">
        <thead>
          <tr>
            <th @click="sortZones('zoneName')">
              <div>
                Zone Name (Types)
                <IconSortable v-show="zoneSort !== 'zoneName'" />
                <IconSortDown v-show="zoneSort === 'zoneName' && zoneSortDesc === true" />
                <IconSortUp v-show="zoneSort === 'zoneName' && zoneSortDesc === false" />
              </div>
            </th>
            <th @click="sortZones('engaged')">
              <div>
                Engaged
                <IconSortable v-show="zoneSort !== 'engaged'" />
                <IconSortDown v-show="zoneSort === 'engaged' && zoneSortDesc === true" />
                <IconSortUp v-show="zoneSort === 'engaged' && zoneSortDesc === false" />
              </div>
            </th>
            <th @click="sortZones('passerby')">
              <div>
                Passerby
                <IconSortable v-show="zoneSort !== 'passerby'" />
                <IconSortDown v-show="zoneSort === 'passerby' && zoneSortDesc === true" />
                <IconSortUp v-show="zoneSort === 'passerby' && zoneSortDesc === false" />
              </div>
            </th>
            <th>Sensor Name (Address)</th>
          </tr>
        </thead>
        <tbody>
          <ReviewSensor
            v-for="sensor in sortedZones"
            :key="sensor.name"
            :sensor="sensor"
            :sensorDistanceOptions="sensorDistanceOptions"
          />
        </tbody>
      </table>
    </div>
    <div v-else class="event-review-section__content">
      <div class="event-review__empty-message">No zones created</div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import ReviewSensor from '@/components/events/ReviewSensor.vue';

import type { EventTypeNew } from '@/types/Event';
import type { SensorDistanceSettingType, SensorZone } from '@/types/Sensor';

import IconSortable from '@/icons/arrows/IconSortable.svg?component';
import IconSortDown from '@/icons/arrows/IconSortDown.svg?component';
import IconSortUp from '@/icons/arrows/IconSortUp.svg?component';

const props = defineProps<{
  newEvent: EventTypeNew;
  sensorDistanceOptions: SensorDistanceSettingType[];
}>();

const zoneSort = ref('zoneName');
const zoneSortDesc = ref(false);

const sortZones = (field: string) => {
  if (zoneSort.value === field) {
    zoneSortDesc.value = !zoneSortDesc.value;
  } else {
    zoneSort.value = field;
    zoneSortDesc.value = false;
  }
};

const sortedZones = computed(() => {
  const allZones: SensorZone[] = [];

  props.newEvent.zones.forEach((zone) => {
    if (zone.sensor) {
      const newSensor = {
        ...zone.sensor,
        zoneName: zone.name,
        zoneCats: zone.categories,
      };

      allZones.push(newSensor);
    } else {
      zone.sensors?.forEach((sensor) => {
        const newSensor = {
          ...sensor,
          zoneName: zone.name,
          zoneCats: zone.categories,
        };

        allZones.push(newSensor);
      });
    }
  });

  return allZones.sort((a, b) => {
    if (zoneSort.value === 'engaged') {
      const aEngaged = a.engaged_signal ? a.engaged_signal : 0;
      const bEngaged = b.engaged_signal ? b.engaged_signal : 0;

      if (zoneSortDesc.value === false) {
        return bEngaged - aEngaged;
      } else {
        return aEngaged - bEngaged;
      }
    } else if (zoneSort.value === 'passerby') {
      const aPasserby = a.passerby_signal ? a.passerby_signal : 0;
      const bPasserby = b.passerby_signal ? b.passerby_signal : 0;

      if (zoneSortDesc.value === false) {
        return bPasserby - aPasserby;
      } else {
        return aPasserby - bPasserby;
      }
    } else if (zoneSort.value === 'zoneName') {
      const aName = a.zoneName ? a.zoneName.toUpperCase() : '';
      const bName = b.zoneName ? b.zoneName.toUpperCase() : '';

      if (zoneSortDesc.value === false) {
        return aName < bName ? -1 : aName > bName ? 1 : 0;
      } else {
        return aName < bName ? 1 : aName > bName ? -1 : 0;
      }
    }
    return 0;
  });
});
</script>
